/**
 * The one place the finance module's user-facing nouns are spelled.
 *
 * Plan 034 D3 renamed what people read, not what anything is called in code:
 * an expense claim reads "Refund" (money someone already spent out of pocket)
 * and a `fund` requisition reads "Requisition" (a specific spend against a
 * budget). DB enums, route paths, capability names and API field names are all
 * unchanged — only these strings moved.
 *
 * There is no i18n layer in this app, so this module is it. Import from here
 * rather than typing the word again; the next rename should be one diff.
 */
import type { RequisitionType } from '@/types'

/** An expense claim — money a person already spent and wants back. */
export const REFUND = 'Refund'
export const REFUND_PLURAL = 'Refunds'
/** Asking for money before spending it, against a budget. */
export const REQUISITION = 'Requisition'
export const REQUISITION_PLURAL = 'Requisitions'

/**
 * `budget` requisitions are hidden from the new-requisition form (D4) but old
 * rows still render in history and approvals, so this has to keep naming them.
 */
export function requisitionTypeLabel(type: RequisitionType | string): string {
  return type === 'budget' ? 'Budget requisition' : REQUISITION
}

/**
 * The one-step "approve, then record the payment" action on the approvals
 * page. Same words for refunds and requisitions: both land in "Disbursed"
 * and the payment dialog's own button reads "Record disbursement".
 */
export const APPROVE_AND_DISBURSE = 'Approve and disburse'

/** Plural of the `budget` requisition subtype label above — same rows, a filter list. */
export const BUDGET_REQUISITION_PLURAL = 'Budget requisitions'
